import React, { Fragment, CSSProperties, useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "antd";
import "./menu.scss";
import { getChildren, getDefaultValues, getComponents, unlinkAsset } from '../helpers'
import { setContextMenuVisibility } from "../../../../../stateManager/store/contextMenuv2/action";
import { groupElements, pasteCopiedElemenet, pushFormActionStack, removeElement, undoDeleteElement, ungroupElements, applyStyles } from "../../../../../stateManager/store/formCreation_v2/actions";
import { useToast } from "../../../../../helpers/commonFunction";
import { logger } from "../../../../../constant/winston";
import { getRandomId } from "../../../../../utils/random-id";
import { clearSelectedItems } from "../../../../../stateManager/store/contextMenu/action";
import { AppState } from "../../../../../stateManager/store";
import { containers } from "../constants";
import { myRedoStack, myUndoStack } from "../stack/Stack";
import { FormCreationData } from "../../../../../stateManager/store/formCreation_v2/types";

enum ComponentType {
  Tab = "Tab",
  TabBar = "TabBar"
}

const StyledContextMenu = styled.div`
  position: absolute;
  top: ${(props: any) => props.theme.top + "px"};
  left: ${(props: any) => props.theme.left + "px"};
  z-index: 99999;
  width: 160px;
  color: inherit;
  padding: 8px 0;
  border-radius: 8px;
  border: 1px solid #1890ff;
  background-color: #fff;
  font-size: 16px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
`;

const iconStyle: CSSProperties = {
  fontSize: 16,
  marginRight: 8,
  position: "relative",
};

export const ContextMenu = () => {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const visibility = useSelector((state: AppState) => state.contextMenu.visibility)
  const isAssetInstanceSelected = useSelector((state: AppState) => state.formJson.isAssetInstanceSelected)
  const assetEditModeEnabled = useSelector((state: AppState) => state.formJson.assetEditMode.isEnabled)
  const selectedItemId = useSelector((state: AppState) => state.formJson.selectedElementId)
  const selectedItemsId = useSelector((state: AppState) => state.formJson.selectedElementsId)
  const deviceType = useSelector((state: AppState) => state.formJson.deviceType)
  const isMultipleSelection = useSelector((state: AppState) => state.contextMenuv2.isMultipleSelect)
  const coordinates = useSelector((state: AppState) => state.contextMenuv2.coordinates)
  const { components, componentOrder } = getComponents()
  const component: FormCreationData = components[selectedItemId] || {};
  const [copiedComponent, setCopiedComponent] = useState<any>()

  useEffect(() => {
    const fetchClipboardData = async () => {
      try {
        const clipboardData = await navigator.clipboard.readText();
        const data = JSON.parse(clipboardData);
        const isValid = validateFormJson(data);
        if (!isValid) {
          ErrorToast("Invalid form json");
          return;
        }
        setCopiedComponent(data);
      } catch (error) {
        logger.error('Failed to read clipboard contents: ', error);
      }
    };

    if (visibility) {
      fetchClipboardData();
    }
  }, [visibility]);

  const hideMenu = () => {
    dispatch(
      setContextMenuVisibility({
        visibility: false,
        coordinates: {},
      })
    );
  };

  const shouldPaste = () => {
    const selectedComp = components[selectedItemId];
    const type = containers.includes(selectedComp?.type)

    // asset can contain only one root element
    if (assetEditModeEnabled && componentOrder.length && !selectedItemId) {
      return false
    }

    return type || !selectedComp
  };

  function getNestedComponents(fields: string[], array: FormCreationData[] = []) {
    fields.forEach((fieldId: string) => {
      const component = components[fieldId];
      array.push(component);
      // recursively all fields
      if (component?.fields?.length) {
        getNestedComponents(component.fields, array);
      }
    })
  }
  // context menu handlers
  const handleDelete = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    const childrenArray: FormCreationData[] = [];
    getNestedComponents(component?.fields, childrenArray);
    const payload: FormCreationData = {
      ...component,
      id: components[selectedItemId].id,
      parentId: components[selectedItemId].parentId || "",
      type: components[selectedItemId].type,
      children: childrenArray,
    }

    if (components[selectedItemId].parentId) {
      const parentId: any = components[selectedItemId].parentId;
      payload.metadata = {
        position: components[parentId].fields.indexOf(components[selectedItemId].id)
      }
    }

    // Undo Redo for delete elements
    myUndoStack.push({ ...payload });
    // component order
    myUndoStack.saveOrder(componentOrder);
    const order = myUndoStack.getOrder();

    dispatch(pushFormActionStack({
      undo: () => {
        const poppedElement = myUndoStack.pop();
        if (poppedElement) {
          myRedoStack.push(poppedElement);
          dispatch(
            undoDeleteElement(poppedElement, order),
          )
        }
      },
      redo: () => {
        const poppedElement = myRedoStack.pop();
        if (poppedElement) {
          myUndoStack.push({ ...poppedElement });
          dispatch(removeElement({
            id: poppedElement.id,
            parentId: poppedElement.parentId || "",
            type: poppedElement.type,
            increment: 1
          }))
        }
      }
    }));
    
    dispatch(removeElement({
      id: components[selectedItemId].id,
      parentId: components[selectedItemId].parentId,
      type: components[selectedItemId].type
    }))
    hideMenu();
  }

  const handleCopy = async () => {
    const rootComponent = { ...components[selectedItemId] }
    const copiedComponents: any = {
      [selectedItemId]: rootComponent
    }

    const componentIds = getChildren(rootComponent.fields, components)
    componentIds.forEach(id => {
      copiedComponents[id] = { ...components[id] }
    })

    const data = {
      componentOrder: [selectedItemId],
      components: copiedComponents,
    }

    try {
      setCopiedComponent(data)
      await navigator.clipboard.writeText(JSON.stringify(data));
      SuccessToast("Copied to clipboard");
      hideMenu();
    } catch (err) {
      console.error("failed to copy text: ", err);
    }
  };

  const validateFormJson = (data: any): boolean => {
    if (Object.keys(data).length === 0) {
      return false
    }
    return true;
  }

  // const randomString = randomId.split("").splice(randomId.length - 2).join("");
  const randomString = getRandomId();
  const modifyIds = (components: any) => {
    for (let key in components) {
      const component = { ...components[key] };
      delete components[key];
      const id = key + randomString;
  
      if (component.parentId) {
        component.parentId = component.parentId + randomString;
      }

      if (component.id) {
        component.id = component.id + randomString;
      }
  
      if (Array.isArray(component.fields)) {
        component.fields = component.fields.map((item: any) => item + randomString);
      }
  
      components[id] = {
        ...component,
      };
    }
    return components
  };

  const handlePaste = async () => {
    try {
      const clipboardData = await navigator.clipboard.readText();
      const data: {
        componentOrder: any; components: { [key: string]: { type: ComponentType } }
      } = JSON.parse(clipboardData);
      const isValid: boolean = validateFormJson(data);
      if (!isValid) {
        ErrorToast("Invalid form json");
        return;
      }
      const selectedComp = components[selectedItemId];
      if (selectedComp?.type === ComponentType.TabBar) {
        const containsNonTabElements = Object.values(data.components).some((component: { type: ComponentType }) => component.type !== ComponentType.Tab)
        if (containsNonTabElements) {
          ErrorToast("Invalid json");
          return;
        }
      }

      let rootId = data.componentOrder[0] + randomString;
      const modifiedData = modifyIds(data.components);

      if (containers.includes(selectedComp?.type)) {
        modifiedData[rootId].parentId = selectedItemId
      } else if (!selectedComp) {
        delete modifiedData[rootId].parentId
      } else {
        modifiedData[rootId].parentId = selectedComp.parentId
      }

      const payload: any = {
        rootId,
        components: modifiedData,
        componentOrder: data.componentOrder,
      }
      myUndoStack.push(payload);
      myRedoStack.clear();
      dispatch(pushFormActionStack({
        undo: () => {
          const poppedElement = myUndoStack.pop();
          if (poppedElement) {
            myRedoStack.push(poppedElement);
            dispatch(
              removeElement({
                id: poppedElement.rootId,
                parentId: poppedElement.components[rootId]?.parentId,
                type: poppedElement.components[rootId]?.type,
                increment: -1
              }),
            )
          }
        },
        redo: () => {
          const poppedElement = myRedoStack.pop();
          if (poppedElement) {
            myUndoStack.push({ ...poppedElement });
            dispatch(pasteCopiedElemenet({
              rootId: poppedElement.rootId,
              componentOrder: poppedElement.componentOrder,
              components: poppedElement.components,
              increment: 1
            }));
          }
        }
      }));
      dispatch(pasteCopiedElemenet(payload))
      SuccessToast("Pasted");
      hideMenu();
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        ErrorToast("Permission denied. Please allow clipboard access.");
      } else {
        ErrorToast("Invalid json");
      }
      logger.error(err);
      hideMenu();
    }
  };

  const shouldApplyStyle = () => {
    if(selectedItemId && copiedComponent && components[selectedItemId]){
      const selectedComp = JSON?.parse(JSON?.stringify(components[selectedItemId]));
      const copiedComp = copiedComponent.components[copiedComponent.componentOrder[0]];
      if (copiedComp.type === selectedComp.type) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  };

  const handlePasteStyles = async () => {
    try {
      const clipboardData = await navigator.clipboard.readText();
      const data = JSON.parse(clipboardData);
      const isValid = validateFormJson(data);
  
      if (!isValid) {
        ErrorToast("Invalid form json");
        return;
      }
  
      const selectedComp = JSON?.parse(JSON?.stringify(components[selectedItemId]));
      const copiedComp = data.components[data.componentOrder[0]];
      const modifiedData = JSON.parse(JSON.stringify(components));
  
      if (copiedComp.type === selectedComp.type) {
        modifiedData[selectedItemId] = { ...selectedComp };
      } else {
        ErrorToast("Widget mismatched");
        return;
      }
  
      const styles = await copiedComp?.properties?.styles;
      modifiedData[selectedItemId].properties.styles = styles;
  
      const currentState: any = {
        rootId: selectedItemId,
        components: JSON.parse(JSON.stringify(components)),
        componentOrder: [...data.componentOrder]
      };
  
      const newState: any = {
        rootId: selectedItemId,
        components: modifiedData,
        componentOrder: data.componentOrder
      };
  
      myUndoStack.push(currentState);
      myRedoStack.clear();
  
      dispatch(pushFormActionStack({
        undo: () => {
          const poppedElement = myUndoStack.pop();
          if (poppedElement) {
            myRedoStack.push(newState);
            dispatch(applyStyles({
              rootId: poppedElement.rootId,
              componentOrder: poppedElement.componentOrder,
              components: poppedElement.components,
              increment: -1,
            }));
          }
        },
        redo: () => {
          const poppedElement = myRedoStack.pop();
          if (poppedElement) {
            myUndoStack.push(currentState);
            dispatch(applyStyles({
              rootId: poppedElement.rootId,
              componentOrder: poppedElement.componentOrder,
              components: poppedElement.components,
              increment: 1,
            }));
          }
        }
      }));
  
      SuccessToast("Pasted");
      dispatch(applyStyles(newState));
      hideMenu();
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        ErrorToast("Permission denied. Please allow clipboard access.");
      } else {
        ErrorToast("Invalid json");
      }
      logger.error(err);
      hideMenu();
    }
  };

  // TODO: you can only group same level sibling elements
  const handleGroup = () => {
    const [groupElId] = selectedItemsId
    const groupEl: any = components[groupElId]

    const grid = {
      type: 'Grid',
      label: 'Grid',
      isGroup: true,
      id: getRandomId(),
      ...getDefaultValues("Grid", deviceType, groupEl.parentId),
      fields: [...selectedItemsId],
    }

    dispatch(groupElements({
      grid,
      parentId: groupEl.parentId,
    }))
    dispatch(clearSelectedItems())
    SuccessToast("Grouped");
    hideMenu();
  }
  const handleUngroup = () => {
    const groupEl = components[selectedItemId]
    const grid = {
      id: components[selectedItemId].id,
      parentId: components[selectedItemId].parentId,
      type: components[selectedItemId].type,
      fields: components[selectedItemId].fields,
    }

    dispatch(ungroupElements({
      grid,
      parentId: groupEl.id,
    }))

    SuccessToast("Ungrouped");
    hideMenu();
    dispatch(clearSelectedItems())
  }

  const handleUnlink = (selectedItemId: string) => {
    unlinkAsset(selectedItemId)
    SuccessToast("Unlinked asset");
    hideMenu();
  }

  return (
    <Fragment>
      { visibility ? (
        <StyledContextMenu theme={coordinates}>
          <ul className="contextMenuWrapper">
            {selectedItemId && !isMultipleSelection && !isAssetInstanceSelected && (
              <li id="contextMenuItem" onClick={handleCopy}>
                <Icon type="copy" style={iconStyle} /> Copy
              </li>
            )}

            {shouldPaste() && !isAssetInstanceSelected && <li id="contextMenuItem" onClick={handlePaste}>
              <Icon type="plus" style={iconStyle} /> Paste
            </li>}    
            {shouldApplyStyle() &&       
              <li id="contextMenuItem" onClick={handlePasteStyles}>
                <Icon type="plus" style={iconStyle} /> Paste Styles
              </li>
            }

            {selectedItemId && component.isAsset && !isMultipleSelection && (
              <li id="contextMenuItem" onClick={() => handleUnlink(selectedItemId)}>
                <Icon type="api" style={iconStyle} /> Unlink
              </li>
            )}

            {isMultipleSelection && selectedItemsId.length >= 2 && (
              <li id="contextMenuItem" onClick={handleGroup}>
                <Icon type="snippets" style={iconStyle} /> Group
              </li>
            )}

            {selectedItemId && !isMultipleSelection && components[selectedItemId]?.isGroup && (
              <li id="contextMenuItem" onClick={handleUngroup}>
                <Icon type="disconnect" style={iconStyle} /> Ungroup
              </li>
            )}

            {component.id && !component.isChildAsset && !isMultipleSelection && (
              <li id="contextMenuItem" onClick={handleDelete}>
                <Icon type="delete" style={iconStyle} /> Delete
              </li>
            )}
          </ul>
        </StyledContextMenu>
      ) : null}
    </Fragment>
  );
};
